import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, AlertCircle, Eye, EyeOff, ArrowLeft, CheckCircle2, Zap } from 'lucide-react';
import logo from '../assets/logo-rmax.jpeg';
import { safeStorage } from '../utils/safeStorage';

interface AuthProps {
}

function withTimeout<T = any>(promise: Promise<T>, ms = 12000): Promise<T> {
  let timer: any;
  const timeout = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error('TIMEOUT_EXCEEDED')), ms);
  });
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    timeout
  ]);
}

export default function Auth({ }: AuthProps) {
  const navigate = useNavigate();
  const { isPasswordRecovery, setIsPasswordRecovery } = useAuth();

  const [authMethod, setAuthMethod] = useState<'magic-link' | 'password'>('magic-link');
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot-password' | 'reset-password'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [coupon, setCoupon] = useState('');
  const [couponStatus, setCouponStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid' | 'error'>('idle');
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [selectedNicheName, setSelectedNicheName] = useState<string | null>(() => {
    try {
      return safeStorage.getItem('rmax_pending_niche_name') || null;
    } catch {
      return null;
    }
  });

  // Estado para e-mail com Magic Link pendente e temporizador de cooldown
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0);

  // Bloqueio defensivo progressivo contra força bruta de senhas (OWASP Rate Limit)
  const [loginLockoutSeconds, setLoginLockoutSeconds] = useState<number>(0);

  // Carrega e-mail pendente e calcula cooldown restante ao montar
  React.useEffect(() => {
    const savedPending = localStorage.getItem('rmax_pending_magic_email');
    const sentAt = localStorage.getItem('rmax_magic_sent_at');

    if (savedPending) {
      setPendingEmail(savedPending);
      setEmail(savedPending);
    }

    if (sentAt) {
      const elapsed = Math.floor((Date.now() - Number(sentAt)) / 1000);
      const remaining = Math.max(0, 60 - elapsed);
      setCooldownSeconds(remaining);
    }

    const lockedUntil = sessionStorage.getItem('rmax_login_locked_until');
    if (lockedUntil) {
      const remaining = Math.max(0, Math.ceil((Number(lockedUntil) - Date.now()) / 1000));
      setLoginLockoutSeconds(remaining);
    }
  }, []);

  // Intervalo do temporizador de cooldown (60s) para magic link e recuperação
  React.useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  // Intervalo do temporizador de lockout de login com senha (60s após 5 falhas)
  React.useEffect(() => {
    if (loginLockoutSeconds <= 0) return;
    const timer = setInterval(() => {
      setLoginLockoutSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          sessionStorage.removeItem('rmax_login_locked_until');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [loginLockoutSeconds]);

  // Captura o cupom de indicação da URL ou do localStorage, além de vertical e plano cirúrgicos (Fast-Track)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const verticalParam = params.get('vertical');
    const planParam = params.get('plan');
    const redirectParam = params.get('redirect');
    const nicheParam = params.get('niche');

    if (nicheParam) {
      const savedNicheName = safeStorage.getItem('rmax_pending_niche_name');
      setSelectedNicheName(savedNicheName || nicheParam);
    } else {
      const savedNicheName = safeStorage.getItem('rmax_pending_niche_name');
      if (savedNicheName) setSelectedNicheName(savedNicheName);
    }

    if (redirectParam) {
      safeStorage.setItem('rmax_pending_redirect', redirectParam);
    }

    if (ref) {
      const cleanRef = ref.trim().toUpperCase();
      localStorage.setItem('rmax_referred_coupon', cleanRef);
      setCoupon(cleanRef);
      setShowCouponInput(true);
    } else {
      const saved = localStorage.getItem('rmax_referred_coupon');
      if (saved) {
        setCoupon(saved.trim().toUpperCase());
        setShowCouponInput(true);
      }
    }

    if (verticalParam) {
      localStorage.setItem('rmax_pending_vertical', verticalParam.trim());
      localStorage.setItem('rmax_enterprise_vertical', verticalParam.trim());
    }
    if (planParam) {
      localStorage.setItem('rmax_pending_plan', planParam.trim());
    }
  }, []);

  // Detecta se a URL solicita recuperação de senha, cadastro ou modo específico
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    const mode = params.get('mode');

    if (mode === 'reset-password' || hash.includes('type=recovery') || isPasswordRecovery) {
      setAuthMode('reset-password');
      setAuthMethod('password');
    } else if (mode === 'signup' || mode === 'register') {
      setAuthMode('signup');
      setAuthMethod('password');
    } else if (mode === 'forgot-password') {
      setAuthMode('forgot-password');
      setAuthMethod('password');
    }
  }, [isPasswordRecovery]);

  // Escuta evento de recuperação de senha do Supabase
  React.useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setAuthMode('reset-password');
        setAuthMethod('password');
        setMessage(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Validação assíncrona do cupom com debounce de 500ms
  React.useEffect(() => {
    const cleanCoupon = coupon.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!cleanCoupon) {
      setCouponStatus('idle');
      return;
    }

    setCouponStatus('validating');
    const delayDebounceFn = setTimeout(async () => {
      // 1. Tenta RPC check_coupon_exists
      try {
        const { data, error } = await supabase
          .rpc('check_coupon_exists', { p_code: cleanCoupon });

        if (!error && typeof data === 'boolean') {
          setCouponStatus(data ? 'valid' : 'invalid');
          return;
        }
        if (error) {
          console.warn('RPC check_coupon_exists error in Auth.tsx, checking via table:', error);
        }
      } catch (rpcErr) {
        console.warn('RPC check_coupon_exists exception in Auth.tsx:', rpcErr);
      }

      // 2. Fallback: consulta direta na tabela influencers
      try {
        const { data, error: tableErr } = await supabase
          .from('influencers')
          .select('id')
          .ilike('code', cleanCoupon)
          .maybeSingle();

        if (!tableErr && data) {
          setCouponStatus('valid');
        } else if (!tableErr && !data) {
          setCouponStatus('invalid');
        } else {
          console.warn('Fallback table check warning in Auth.tsx:', tableErr);
          setCouponStatus('valid'); // Não travar o cadastro caso ocorra erro transitório
        }
      } catch (tableErr) {
        console.warn('Error validating coupon fallback in Auth.tsx:', tableErr);
        setCouponStatus('valid');
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [coupon]);

  const handleClearPendingEmail = () => {
    localStorage.removeItem('rmax_pending_magic_email');
    localStorage.removeItem('rmax_magic_sent_at');
    setPendingEmail(null);
    setEmail('');
    setCooldownSeconds(0);
    setMessage(null);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    const cleanEmail = email.trim().toLowerCase();

    // 1. Registra lead se houver cupom (com try-catch isolado)
    const cleanCouponUpper = (coupon || localStorage.getItem('rmax_referred_coupon') || '').trim().toUpperCase();
    if (cleanCouponUpper && cleanEmail) {
      localStorage.setItem('rmax_referred_coupon', cleanCouponUpper);
      try {
        const { error: leadError } = await supabase
          .from('coupon_leads')
          .insert({ email: cleanEmail, coupon_code: cleanCouponUpper });
        
        if (leadError && leadError.code !== '23505' && import.meta.env.DEV) {
          console.warn('[coupon_leads] insert falhou:', leadError.code);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[coupon_leads] erro no try-catch isolado');
        }
      }
    }

    try {
      if (authMode === 'forgot-password') {
        if (cooldownSeconds > 0) {
          throw new Error(`Por favor, aguarde ${cooldownSeconds} segundos antes de solicitar um novo link de recuperação.`);
        }
        const cleanEmail = email.trim().toLowerCase();
        if (!cleanEmail) {
          throw new Error('Informe seu e-mail para recuperar a senha.');
        }

        const redirectUrl = `${window.location.origin}/auth?mode=reset-password`;
        const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
          redirectTo: redirectUrl,
        });

        if (error) throw error;

        setForgotPasswordSent(true);
        setCooldownSeconds(60);
        localStorage.setItem('rmax_magic_sent_at', Date.now().toString());
        setMessage({
          type: 'success',
          text: 'Se o e-mail informado estiver cadastrado no R-MAX, as instruções para redefinição de senha foram enviadas. Verifique sua caixa de entrada e pasta de spam.'
        });
        return;
      }

      if (authMode === 'reset-password') {
        if (password.length < 6) {
          throw new Error('A nova senha deve conter no mínimo 6 caracteres.');
        }
        if (password !== confirmPassword) {
          throw new Error('As senhas não coincidem.');
        }

        const { error } = await supabase.auth.updateUser({
          password: password,
        });

        if (error) throw error;

        setMessage({
          type: 'success',
          text: 'Sua senha foi redefinida com sucesso! Entrando no sistema...'
        });

        setIsPasswordRecovery(false);

        setTimeout(() => {
          navigate('/app');
        }, 1200);
        return;
      }

      if (authMode === 'signup') {
        if (password.length < 6) {
          throw new Error('A senha deve conter no mínimo 6 caracteres.');
        }
        if (password !== confirmPassword) {
          throw new Error('As senhas não coincidem.');
        }

        const { error } = await supabase.auth.signUp({
          email: cleanEmail,
          password,
          options: {
            data: {
              coupon_code: cleanCouponUpper || undefined,
              influencer_code: cleanCouponUpper || undefined
            }
          }
        });

        if (error) throw error;

        setMessage({
          type: 'success',
          text: 'Cadastro realizado com sucesso! Verifique sua caixa de entrada para confirmar o e-mail.'
        });
      } else if (authMethod === 'magic-link') {
        // Bloqueia reenvio prematuro para evitar rate limit (429) do Supabase
        if (cooldownSeconds > 0) {
          throw new Error(`Por favor, aguarde ${cooldownSeconds} segundos antes de solicitar um novo link.`);
        }

        const params = new URLSearchParams(window.location.search);
        const redirectParam = params.get('redirect') || safeStorage.getItem('rmax_pending_redirect') || '/app';
        const safeRedirect = redirectParam.startsWith('/') && !redirectParam.startsWith('//') ? redirectParam : '/app';

        // Usando Magic Link para Atrito Zero e persistindo metadados do cupom
        const { error } = await withTimeout<any>(supabase.auth.signInWithOtp({
          email: cleanEmail,
          options: {
            emailRedirectTo: window.location.origin + safeRedirect,
            data: {
              coupon_code: cleanCouponUpper || undefined,
              influencer_code: cleanCouponUpper || undefined
            }
          }
        }), 12000);
        
        if (error) throw error;
        
        localStorage.setItem('rmax_pending_magic_email', cleanEmail);
        localStorage.setItem('rmax_magic_sent_at', Date.now().toString());
        setPendingEmail(cleanEmail);
        setCooldownSeconds(60);

        setMessage({ 
          type: 'success', 
          text: 'Link enviado com sucesso! Acesse sua caixa de entrada (ou pasta de spam) e clique no link para destravar os módulos.' 
        });
      } else {
        // Bloqueio defensivo progressivo contra força bruta de senhas (OWASP Rate Limit)
        if (loginLockoutSeconds > 0) {
          throw new Error(`Muitas tentativas incorretas. Por segurança, aguarde ${loginLockoutSeconds} segundos antes de tentar novamente ou recupere sua senha.`);
        }

        // Janela deslizante de 15 minutos para reset de tentativas
        const lastFailedAt = Number(sessionStorage.getItem('rmax_last_failed_attempt_at') || '0');
        const now = Date.now();
        let currentAttempts = Number(sessionStorage.getItem('rmax_failed_login_attempts') || '0');
        if (now - lastFailedAt > 15 * 60 * 1000) {
          currentAttempts = 0;
          sessionStorage.removeItem('rmax_failed_login_attempts');
        }

        // Login tradicional com e-mail higienizado e senha intacta (sem trim) com proteção de timeout defensivo de 12s
        const { error } = await withTimeout<any>(supabase.auth.signInWithPassword({
          email: cleanEmail,
          password,
        }), 12000);

        if (error) {
          sessionStorage.setItem('rmax_last_failed_attempt_at', String(now));
          currentAttempts += 1;
          sessionStorage.setItem('rmax_failed_login_attempts', String(currentAttempts));
          if (currentAttempts >= 5) {
            const lockUntil = Date.now() + 60000;
            sessionStorage.setItem('rmax_login_locked_until', String(lockUntil));
            setLoginLockoutSeconds(60);
            sessionStorage.removeItem('rmax_failed_login_attempts');
            sessionStorage.removeItem('rmax_last_failed_attempt_at');
            throw new Error('Limite de 5 tentativas incorretas excedido. Por segurança, aguarde 60 segundos ou use a opção "Esqueci minha senha" abaixo para redefinir seu acesso imediatamente.');
          }
          throw error;
        }

        // Login bem-sucedido: limpa registros de tentativas
        sessionStorage.removeItem('rmax_failed_login_attempts');
        sessionStorage.removeItem('rmax_last_failed_attempt_at');
        sessionStorage.removeItem('rmax_login_locked_until');

        const params = new URLSearchParams(window.location.search);
        const redirectParam = params.get('redirect') || safeStorage.getItem('rmax_pending_redirect') || '/app';
        const safeRedirect = redirectParam.startsWith('/') && !redirectParam.startsWith('//') ? redirectParam : '/app';
        navigate(safeRedirect, { replace: true });
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.warn('Auth Error Status:', error?.status || error?.code || 'AUTH_ERROR');
      }
      let errorText = 'Não conseguimos processar o acesso. Tente novamente mais tarde.';
      const msg = (error?.message || '').toString();
      const is504OrTimeout = error?.status === 504 || msg.includes('504') || msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('gateway') || msg.includes('TIMEOUT_EXCEEDED');
      const isRateLimit = error?.status === 429 || msg.toLowerCase().includes('rate limit');
      const isNetworkError = msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network');
      
      if (authMode === 'forgot-password') {
        if (isRateLimit) {
          errorText = 'Muitas solicitações seguidas. Aguarde 60 segundos antes de tentar novamente.';
        } else if (is504OrTimeout || isNetworkError) {
          errorText = 'Instabilidade na conexão. Verifique sua internet e tente novamente.';
        } else {
          errorText = error.message || 'Não conseguimos enviar o link de recuperação. Verifique o e-mail digitado.';
        }
      } else if (authMode === 'reset-password') {
        errorText = error.message || 'Não foi possível redefinir sua senha. O link pode ter expirado. Solicite uma nova recuperação.';
      } else if (authMode === 'signup') {
        if (is504OrTimeout) {
          errorText = 'O servidor demorou para responder ao criar a conta. Verifique sua conexão e tente novamente.';
        } else if (isNetworkError) {
          errorText = 'Falha de conexão com o servidor. Verifique sua internet móvel ou Wi-Fi e tente novamente.';
        } else if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
          errorText = 'Já existe um cadastro com este e-mail. Acesse a aba "Entrar" ou solicite a recuperação de senha.';
        } else {
          errorText = error.message || 'Erro ao realizar o cadastro. Verifique os dados informados.';
        }
      } else if (authMethod === 'magic-link') {
        if (isRateLimit) {
          errorText = 'Limite de solicitações atingido! Aguarde 60 segundos ou utilize a aba "Entrar com Senha".';
        } else if (is504OrTimeout) {
          errorText = 'O servidor de envio demorou para responder (Tempo limite esgotado). Verifique sua conexão e tente novamente em instantes, ou acesse pela aba "Entrar com Senha".';
        } else if (isNetworkError) {
          errorText = 'Falha de conexão com a rede. Verifique o sinal da sua internet móvel e tente novamente.';
        } else if (msg.startsWith('HTTP ') || msg.length < 10) {
          errorText = 'Não conseguimos enviar o link de acesso no momento. Tente novamente em instantes ou entre com sua senha.';
        } else {
          errorText = error.message || 'Não conseguimos enviar o link. Verifique o e-mail ou tente novamente.';
        }
      } else {
        if (is504OrTimeout || isNetworkError) {
          errorText = 'Instabilidade na conexão. Verifique sua internet e tente novamente.';
        } else if (msg.toLowerCase().includes('tentativas') || msg.toLowerCase().includes('bloqueada')) {
          errorText = msg;
        } else if (msg.toLowerCase().includes('invalid login credentials')) {
          errorText = 'E-mail ou senha incorretos. Verifique suas credenciais ou recupere sua senha.';
        } else {
          errorText = error.message || 'E-mail ou senha incorretos. Verifique suas credenciais.';
        }
      }

      setMessage({ 
        type: 'error', 
        text: errorText 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 relative overflow-hidden font-sans">
      {/* Subtle background glow */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-slate-200/60 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="w-full max-w-md bg-white border border-slate-200 p-8 rounded-3xl shadow-xl shadow-slate-200/50 relative z-10 transition-all hover:border-slate-300">
        <div className="flex flex-col items-center text-center">
          <div className="mb-6">
            <img 
              src={logo} 
              alt="R MAX" 
              className="max-w-28 h-auto drop-shadow-sm rounded-2xl border border-slate-100"
            />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {authMode === 'forgot-password'
              ? 'Recuperar Senha'
              : authMode === 'reset-password'
                ? 'Criar Nova Senha'
                : 'Acesso Rápido R-MAX'}
          </h2>
          <p className="text-slate-600 text-sm">
            {authMode === 'forgot-password'
              ? 'Informe seu e-mail cadastrado para receber as instruções e o link de recuperação.'
              : authMode === 'reset-password'
                ? 'Digite sua nova senha de acesso abaixo.'
                : authMode === 'signup'
                  ? 'Cadastre seu e-mail e escolha uma senha de acesso.'
                  : authMethod === 'magic-link' 
                    ? 'Digite seu e-mail para receber um link de acesso instantâneo. Sem senhas.' 
                    : 'Entre com seu e-mail e senha de acesso direto.'}
          </p>

          {/* Banner de Nicho Selecionado */}
          {selectedNicheName && authMode !== 'reset-password' && authMode !== 'forgot-password' && (
            <div className="w-full mt-4 p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-950 flex items-center justify-between gap-3 text-left">
              <div className="min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                  <Zap className="w-3 h-3 fill-current text-emerald-600" />
                  Ramo Selecionado
                </span>
                <span className="text-xs font-bold text-slate-900 truncate block">{selectedNicheName}</span>
              </div>
              <button
                type="button"
                onClick={() => navigate('/selecionar-perfil')}
                className="text-xs font-bold text-emerald-700 hover:text-emerald-800 underline whitespace-nowrap cursor-pointer shrink-0"
              >
                Trocar ramo
              </button>
            </div>
          )}

          {/* Seletor de Métodos de Login ou Botão Voltar */}
          {authMode === 'forgot-password' || authMode === 'reset-password' ? (
            <div className="w-full flex items-center justify-start mt-4 mb-2">
              <button
                type="button"
                onClick={() => {
                  setAuthMode('login');
                  setForgotPasswordSent(false);
                  setMessage(null);
                }}
                className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" /> Voltar para o login
              </button>
            </div>
          ) : (
            <div className="flex w-full bg-slate-100 p-1 rounded-2xl mb-6 mt-4 border border-slate-200">
              <button
                type="button"
                onClick={() => { setAuthMethod('magic-link'); setAuthMode('login'); setConfirmPassword(''); setMessage(null); }}
                className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  authMethod === 'magic-link' && authMode === 'login'
                    ? 'bg-white text-slate-950 shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Acesso Rápido (Sem Senha)
              </button>
              <button
                type="button"
                onClick={() => { setAuthMethod('password'); setAuthMode('login'); setConfirmPassword(''); setMessage(null); }}
                className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  authMethod === 'password' && authMode === 'login'
                    ? 'bg-white text-slate-950 shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Entrar com Senha
              </button>
            </div>
          )}

          <form onSubmit={handleAuth} className="w-full space-y-4">
            {message && (
              <div className={`p-4 rounded-xl flex items-center gap-3 text-sm font-medium ${
                message.type === 'success' 
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                  : 'bg-rose-50 text-rose-800 border border-rose-200'
              }`}>
                {message.type === 'error' && <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />}
                {message.type === 'success' && <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />}
                <span>{message.text}</span>
              </div>
            )}

            {authMode === 'forgot-password' && forgotPasswordSent ? (
              <div className="space-y-4 text-center py-2 animate-in fade-in duration-200">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-left space-y-2">
                  <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                    <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
                    Link de recuperação enviado!
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Enviamos um e-mail para <strong className="text-slate-900">{email}</strong> com o link seguro para você redefinir sua senha. Verifique também a pasta de spam ou lixo eletrônico.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('login');
                    setForgotPasswordSent(false);
                    setMessage(null);
                  }}
                  className="w-full h-12 bg-slate-100 hover:bg-slate-200 text-slate-900 border border-slate-200 font-bold rounded-2xl transition-all text-sm cursor-pointer"
                >
                  Voltar para o Login
                </button>
              </div>
            ) : (
              <>
                {/* Campo E-mail (quando não estiver redefinindo senha) */}
                {authMode !== 'reset-password' && (
                  <div className="space-y-1 text-left">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">E-mail</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="email"
                        required
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3.5 pl-12 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>
                )}

                {/* Campos para Reset de Senha */}
                {authMode === 'reset-password' && (
                  <>
                    <div className="space-y-1 text-left">
                      <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">Nova Senha</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3.5 pl-12 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all"
                          placeholder="Mínimo 6 caracteres"
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1 text-left">
                      <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">Confirmar Nova Senha</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3.5 pl-12 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all"
                          placeholder="Confirme a nova senha"
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Campos de Senha no Login e Cadastro */}
                {(authMode === 'login' || authMode === 'signup') && (authMethod === 'password' || authMode === 'signup') && (
                  <div className="space-y-1 text-left">
                    <div className="flex items-center justify-between px-1">
                      <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Senha</label>
                      {authMode === 'login' && (
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => {
                            setAuthMode('forgot-password');
                            setMessage(null);
                          }}
                          className="text-xs text-slate-500 hover:text-emerald-700 hover:underline transition-colors font-semibold cursor-pointer"
                        >
                          Esqueci minha senha
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3.5 pl-12 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all"
                        placeholder="Digite sua senha"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {authMode === 'signup' && (
                  <div className="space-y-1 text-left">
                    <label className="text-[10px] font-bold text-slate-600 uppercase tracking-widest ml-1">Confirmar Senha</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3.5 pl-12 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all"
                        placeholder="Confirme sua senha"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {(authMode === 'signup' || (authMethod === 'magic-link' && authMode === 'login' && !pendingEmail)) && (
                  <div className="space-y-2 text-left pt-2">
                    <button
                      type="button"
                      onClick={() => setShowCouponInput(!showCouponInput)}
                      className="text-xs text-slate-500 hover:text-emerald-700 transition-all font-semibold flex items-center gap-1.5 ml-1 cursor-pointer"
                    >
                      {showCouponInput ? 'Remover cupom' : 'Possui um cupom de indicação?'}
                    </button>
                    
                    {showCouponInput && (
                      <div className="space-y-1.5 animate-in fade-in duration-200">
                        <div className="relative">
                          <input
                            type="text"
                            value={coupon}
                            onChange={(e) => setCoupon(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3.5 px-4 text-sm text-slate-900 uppercase focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition-all placeholder:normal-case"
                            placeholder="Ex: INFLUENCER30"
                          />
                          {couponStatus === 'validating' && (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-slate-300 border-t-emerald-600 rounded-full animate-spin" />
                          )}
                        </div>
                        {couponStatus === 'valid' && (
                          <p className="text-xs text-emerald-700 font-bold ml-1">
                            ✓ Cupom ativo! Você terá 30 dias de teste e 30 orçamentos grátis.
                          </p>
                        )}
                        {couponStatus === 'invalid' && (
                          <p className="text-xs text-rose-600 font-bold ml-1">
                            ✗ Cupom não encontrado. Seu cadastro será feito no período padrão de 14 dias (ou 14 orçamentos).
                          </p>
                        )}
                        {couponStatus === 'error' && (
                          <p className="text-xs text-amber-600 font-bold ml-1">
                            ⚠ Instabilidade de rede. Verifique sua conexão ou tente novamente.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {pendingEmail && authMethod === 'magic-link' && authMode === 'login' && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-left space-y-2 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-900">✓ Link de acesso já enviado</span>
                      <button
                        type="button"
                        onClick={handleClearPendingEmail}
                        className="text-[10px] text-slate-600 hover:text-slate-900 underline font-medium cursor-pointer"
                      >
                        Usar outro e-mail
                      </button>
                    </div>
                    <p className="text-xs text-slate-700 leading-relaxed">
                      Confira sua caixa de entrada ou pasta de spam para <strong className="text-emerald-800">{pendingEmail}</strong> e clique no link para destravar seus módulos.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={
                    loading || 
                    (authMethod === 'magic-link' && authMode === 'login' && cooldownSeconds > 0) ||
                    (authMethod === 'password' && authMode === 'login' && loginLockoutSeconds > 0) ||
                    (authMode === 'forgot-password' && cooldownSeconds > 0)
                  }
                  className="w-full h-13 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-[0.98] mt-6 shadow-md shadow-emerald-600/20 cursor-pointer text-sm"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {authMode === 'signup' 
                        ? 'Criar Minha Conta' 
                        : authMode === 'forgot-password'
                          ? (cooldownSeconds > 0 ? `Aguarde ${cooldownSeconds}s para reenviar` : 'Enviar Link de Recuperação')
                          : authMode === 'reset-password'
                            ? 'Salvar Nova Senha'
                            : authMethod === 'magic-link' 
                              ? (cooldownSeconds > 0 ? `Reenviar link em ${cooldownSeconds}s` : 'Receber Link de Acesso') 
                              : (loginLockoutSeconds > 0 ? `Bloqueio temporário (${loginLockoutSeconds}s)` : 'Entrar no Sistema')}
                    </>
                  )}
                </button>
              </>
            )}
          </form>

          {/* Links de Rodapé */}
          {authMode === 'forgot-password' && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  setAuthMode('login');
                  setForgotPasswordSent(false);
                  setMessage(null);
                }}
                className="text-xs text-slate-600 hover:text-emerald-700 transition-all font-medium cursor-pointer"
              >
                Lembrou sua senha? <span className="underline font-bold">Faça login aqui</span>
              </button>
            </div>
          )}

          {authMode === 'reset-password' && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  setAuthMode('login');
                  setMessage(null);
                }}
                className="text-xs text-slate-600 hover:text-emerald-700 transition-all font-medium cursor-pointer"
              >
                Voltar para a tela de login
              </button>
            </div>
          )}

          {(authMode === 'login' || authMode === 'signup') && authMethod === 'password' && (
            <div className="mt-4">
              {authMode === 'login' ? (
                <button
                  type="button"
                  onClick={() => { setAuthMode('signup'); setMessage(null); }}
                  className="text-xs text-slate-600 hover:text-emerald-700 transition-all font-medium cursor-pointer"
                >
                  Ainda não tem conta? <span className="underline font-bold">Cadastre-se aqui</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setAuthMode('login'); setConfirmPassword(''); setMessage(null); }}
                  className="text-xs text-slate-600 hover:text-emerald-700 transition-all font-medium cursor-pointer"
                >
                  Já tem uma conta? <span className="underline font-bold">Faça login aqui</span>
                </button>
              )}
            </div>
          )}

          <div className="w-full mt-6">
            <p className="text-slate-400 text-[10px] uppercase font-bold tracking-widest">
              Garantimos sua privacidade
            </p>
          </div>

          <div className="mt-8 pt-5 border-t border-slate-200 w-full text-[11px] text-slate-400 uppercase tracking-widest font-medium">
            Desenvolvido por R-MAX
          </div>
        </div>
      </div>
    </div>
  );
}
